import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  AlertTriangle,
  FileWarning,
  CheckCircle2,
  Clock,
  ShieldCheck,
  XCircle,
  ShieldAlert,
  FileText,
  Upload,
  ArrowRight,
  ChevronDown,
  Info,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import {
  CompanionProfileLifecycleStatus,
  MissingDocumentSideItem,
  CompanionDocumentEntry,
  validateCompanionDocuments,
  SupportedDocumentType,
  DOCUMENT_REQUIREMENTS_REGISTRY,
  normalizeDocumentType,
  DocumentValidationResult,
} from '../../utils/documentValidation';
import {
  subscribeToCompanionDocuments,
  subscribeToCompanionVerification,
} from '../../lib/verificationService';
import { UserProfile } from '../../types';

export interface ProfileStatusBadgeProps {
  /** Explicit profile status */
  profileStatus?: CompanionProfileLifecycleStatus | string;
  /** Alias for profileStatus */
  status?: CompanionProfileLifecycleStatus | string;
  /** Companion UserProfile from auth context */
  userProfile?: UserProfile | null;
  /** Explicit companionId for live Firestore synchronization */
  companionId?: string;
  /** Documents map override */
  documents?: Record<string, CompanionDocumentEntry | any>;
  /** Explicit missing items if pre-calculated */
  missingItems?: MissingDocumentSideItem[];
  /** Explicit count of missing document sides */
  missingCount?: number;
  /** Navigation handler to verification / document upload center */
  onNavigateToVerification?: () => void;
  /** General click callback */
  onClick?: () => void;
  /** Visual variant */
  variant?: 'badge' | 'pill' | 'notification' | 'banner' | 'chip' | 'compact';
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Whether to show status icon */
  showIcon?: boolean;
  /** Whether to show count of missing sides when documents_incomplete */
  showMissingCount?: boolean;
  /** Whether to allow opening the notification popover with missing document details */
  interactive?: boolean;
  /** Additional CSS class names */
  className?: string;
}

/**
 * ProfileStatusBadge
 * Displays the current profileStatus of a Companion and notifies them
 * of incomplete document statuses with actionable feedback.
 */
export const ProfileStatusBadge: React.FC<ProfileStatusBadgeProps> = ({
  profileStatus: propProfileStatus,
  status: propStatus,
  userProfile,
  companionId: propCompanionId,
  documents: propDocuments,
  missingItems: propMissingItems,
  missingCount: propMissingCount,
  onNavigateToVerification,
  onClick,
  variant = 'badge',
  size = 'md',
  showIcon = true,
  showMissingCount = true,
  interactive = true,
  className = '',
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsPopoverOpen(false);
      }
    };
    if (isPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopoverOpen]);

  // Resolve Companion ID
  const effectiveCompanionId = useMemo(() => {
    if (propCompanionId) return propCompanionId;
    if ((userProfile as any)?.uid) return (userProfile as any).uid;
    if (userProfile?.email) return userProfile.email.replace(/[^a-zA-Z0-9]/g, '_');
    return 'CP1024';
  }, [propCompanionId, userProfile]);

  // Real-time document subscription state
  const [liveDocs, setLiveDocs] = useState<Record<string, any>>(() => {
    if (propDocuments) return propDocuments;
    try {
      const cached = localStorage.getItem(`pairup_verification_docs_${effectiveCompanionId}`);
      if (cached) return JSON.parse(cached);
    } catch {}
    return {};
  });

  const [liveVerificationProfile, setLiveVerificationProfile] = useState<any>(null);

  useEffect(() => {
    if (propDocuments) {
      setLiveDocs(propDocuments);
      return;
    }
    if (!effectiveCompanionId) return;

    const unsubDocs = subscribeToCompanionDocuments(effectiveCompanionId as any, (docs) => {
      setLiveDocs(docs);
    });

    const unsubProfile = subscribeToCompanionVerification(effectiveCompanionId, (p) => {
      setLiveVerificationProfile(p);
    });

    return () => {
      unsubDocs();
      unsubProfile();
    };
  }, [effectiveCompanionId, propDocuments]);

  // Derive consolidated documents mapping
  const documentsMap = useMemo<Record<string, CompanionDocumentEntry>>(() => {
    const map: Record<string, CompanionDocumentEntry> = { ...liveDocs };

    // Check userProfile for driving license fallback
    const dlDetails = userProfile?.drivingLicenceDetails || userProfile?.companionProfile?.drivingLicence;
    if (dlDetails) {
      const dlKey = 'driving_license';
      const existingDl = map[dlKey] || {};
      map[dlKey] = {
        ...existingDl,
        idNumber: dlDetails.licenceNumber || (existingDl as any).idNumber,
        front: dlDetails.frontUrl || existingDl.front,
        back: dlDetails.backUrl || existingDl.back,
      };
    }

    // Check userProfile for primary ID fallback
    const compPrimaryDoc = userProfile?.companionProfile;
    if (compPrimaryDoc?.idType && compPrimaryDoc?.idDocumentUrl) {
      const normalizedType = normalizeDocumentType(compPrimaryDoc.idType);
      const existing = map[normalizedType] || {};
      map[normalizedType] = {
        ...existing,
        idNumber: compPrimaryDoc.idNumber || (existing as any).idNumber,
        front: existing.front || compPrimaryDoc.idDocumentUrl,
        back: existing.back || (compPrimaryDoc as any).backUrl || (compPrimaryDoc as any).idBackDocumentUrl,
      };
    }

    return map;
  }, [liveDocs, userProfile]);

  // Determine driver status & primary doc
  const isDriver = useMemo(() => {
    const services =
      userProfile?.companionProfile?.services ||
      (userProfile?.services ? userProfile.services.map((s) => s.name) : []);
    return (
      Boolean(userProfile?.isDriverApproved) ||
      services.some((s) => typeof s === 'string' && s.toLowerCase().includes('driver'))
    );
  }, [userProfile]);

  const selectedPrimaryDocType = useMemo(() => {
    return (
      userProfile?.companionProfile?.idType ||
      liveVerificationProfile?.primaryDocType ||
      'aadhaar'
    );
  }, [userProfile, liveVerificationProfile]);

  // Execute centralized validation utility
  const validationResult: DocumentValidationResult = useMemo(() => {
    const currentAdminVerdict =
      liveVerificationProfile?.verificationStatus === 'verified' || userProfile?.isIdVerified
        ? 'verified'
        : liveVerificationProfile?.verificationStatus === 'rejected'
        ? 'rejected'
        : null;

    const isProfileInfoComplete = Boolean(
      userProfile?.displayName || userProfile?.name || userProfile?.companionProfile?.displayName
    );

    const verificationStarted =
      liveVerificationProfile?.verificationStatus === 'under_review' ||
      liveVerificationProfile?.verificationStatus === 'submitted';

    return validateCompanionDocuments(documentsMap, {
      selectedPrimaryDocType,
      isDriverService: isDriver,
      isProfileInfoComplete,
      verificationStarted,
      currentAdminVerdict,
    });
  }, [documentsMap, selectedPrimaryDocType, isDriver, userProfile, liveVerificationProfile]);

  // Determine authoritative profile status
  const effectiveStatus: CompanionProfileLifecycleStatus = useMemo(() => {
    const explicit = propProfileStatus || propStatus;
    if (explicit && typeof explicit === 'string') {
      const normalized = explicit.toLowerCase();
      if (
        ['draft', 'documents_incomplete', 'documents_uploaded', 'under_review', 'verified', 'rejected', 'suspended'].includes(
          normalized
        )
      ) {
        return normalized as CompanionProfileLifecycleStatus;
      }
    }
    if (
      userProfile?.profileStatus &&
      ['draft', 'documents_incomplete', 'documents_uploaded', 'under_review', 'verified', 'rejected', 'suspended'].includes(
        userProfile.profileStatus as string
      )
    ) {
      return userProfile.profileStatus as CompanionProfileLifecycleStatus;
    }
    return validationResult.profileStatus;
  }, [propProfileStatus, propStatus, userProfile?.profileStatus, validationResult.profileStatus]);

  // Consolidated missing items
  const missingItems = useMemo<MissingDocumentSideItem[]>(() => {
    if (propMissingItems) return propMissingItems;
    return validationResult.missingItems || [];
  }, [propMissingItems, validationResult.missingItems]);

  const missingCount = useMemo<number>(() => {
    if (propMissingCount !== undefined) return propMissingCount;
    return missingItems.length;
  }, [propMissingCount, missingItems]);

  const isDocumentsIncomplete = effectiveStatus === 'documents_incomplete';

  // Config mapping for styling and labels
  const config = useMemo(() => {
    switch (effectiveStatus) {
      case 'documents_incomplete':
        return {
          label: 'Documents Incomplete',
          badgeText: 'DOCUMENTS INCOMPLETE',
          shortText: 'Incomplete',
          bg: 'bg-amber-950/80 hover:bg-amber-900/80 text-amber-300 border-amber-600/70',
          dot: 'bg-amber-400 animate-pulse',
          icon: AlertTriangle,
          iconColor: 'text-amber-400',
          cardBg: 'bg-gradient-to-r from-amber-950/90 via-slate-900 to-rose-950/80 border-amber-500/60',
          textColor: 'text-amber-200',
          alertTitle: 'Action Required: Incomplete Mandatory Documents',
          alertDesc:
            'All mandatory government ID sides and pages must be uploaded before your profile can be submitted for review.',
        };
      case 'documents_uploaded':
        return {
          label: 'Documents Uploaded',
          badgeText: 'DOCUMENTS UPLOADED',
          shortText: 'Uploaded',
          bg: 'bg-blue-950/80 hover:bg-blue-900/80 text-blue-300 border-blue-600/70',
          dot: 'bg-blue-400',
          icon: Upload,
          iconColor: 'text-blue-400',
          cardBg: 'bg-gradient-to-r from-blue-950/90 via-slate-900 to-slate-900 border-blue-500/60',
          textColor: 'text-blue-200',
          alertTitle: 'All Mandatory Documents Uploaded',
          alertDesc: 'Your document pages are complete and ready for verification submission.',
        };
      case 'under_review':
        return {
          label: 'Under Review',
          badgeText: 'UNDER REVIEW',
          shortText: 'Under Review',
          bg: 'bg-purple-950/80 hover:bg-purple-900/80 text-purple-300 border-purple-600/70',
          dot: 'bg-purple-400 animate-pulse',
          icon: Clock,
          iconColor: 'text-purple-400',
          cardBg: 'bg-gradient-to-r from-purple-950/90 via-slate-900 to-slate-900 border-purple-500/60',
          textColor: 'text-purple-200',
          alertTitle: 'Verification In Progress',
          alertDesc: 'The PairUpToday Trust & Safety team is reviewing your uploaded documents.',
        };
      case 'verified':
        return {
          label: 'Verified',
          badgeText: 'VERIFIED ✓',
          shortText: 'Verified',
          bg: 'bg-emerald-950/80 hover:bg-emerald-900/80 text-emerald-300 border-emerald-600/70',
          dot: 'bg-emerald-400',
          icon: ShieldCheck,
          iconColor: 'text-emerald-400',
          cardBg: 'bg-gradient-to-r from-emerald-950/90 via-slate-900 to-slate-900 border-emerald-500/60',
          textColor: 'text-emerald-200',
          alertTitle: 'Profile Fully Verified',
          alertDesc: 'All documents verified. Your companion profile is active and discoverable.',
        };
      case 'rejected':
        return {
          label: 'Action Required / Rejected',
          badgeText: 'RE-UPLOAD REQUIRED',
          shortText: 'Rejected',
          bg: 'bg-rose-950/80 hover:bg-rose-900/80 text-rose-300 border-rose-600/70',
          dot: 'bg-rose-400 animate-ping',
          icon: XCircle,
          iconColor: 'text-rose-400',
          cardBg: 'bg-gradient-to-r from-rose-950/90 via-slate-900 to-slate-900 border-rose-500/60',
          textColor: 'text-rose-200',
          alertTitle: 'Document Re-upload Required',
          alertDesc: 'One or more submitted document sides were rejected. Please review and re-upload.',
        };
      case 'suspended':
        return {
          label: 'Suspended',
          badgeText: 'SUSPENDED',
          shortText: 'Suspended',
          bg: 'bg-red-950/90 text-red-300 border-red-700',
          dot: 'bg-red-500',
          icon: ShieldAlert,
          iconColor: 'text-red-400',
          cardBg: 'bg-slate-900 border-red-800',
          textColor: 'text-red-200',
          alertTitle: 'Account Suspended',
          alertDesc: 'This companion account has been suspended. Please contact platform support.',
        };
      case 'draft':
      default:
        return {
          label: 'Draft',
          badgeText: 'DRAFT',
          shortText: 'Draft',
          bg: 'bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 border-slate-600',
          dot: 'bg-slate-400',
          icon: FileText,
          iconColor: 'text-slate-400',
          cardBg: 'bg-slate-900 border-slate-800',
          textColor: 'text-slate-300',
          alertTitle: 'Profile Draft Incomplete',
          alertDesc: 'Complete your profile details and upload mandatory government ID documents.',
        };
    }
  }, [effectiveStatus]);

  const StatusIcon = config.icon;

  // Size styling
  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'xs':
        return 'text-[10px] px-2 py-0.5 gap-1';
      case 'sm':
        return 'text-[11px] px-2.5 py-1 gap-1.5';
      case 'lg':
        return 'text-sm px-4 py-2 gap-2.5';
      case 'md':
      default:
        return 'text-xs px-3 py-1.5 gap-2';
    }
  }, [size]);

  const dotSize = size === 'xs' ? 'w-1.5 h-1.5' : size === 'lg' ? 'w-2.5 h-2.5' : 'w-2 h-2';
  const iconSize = size === 'xs' ? 'w-3 h-3' : size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  const handleAction = () => {
    if (onClick) {
      onClick();
    } else if (onNavigateToVerification) {
      onNavigateToVerification();
    }
  };

  // =========================================================================
  // VARIANT: NOTIFICATION / BANNER (Notifies of incomplete document status)
  // =========================================================================
  if (variant === 'notification' || variant === 'banner') {
    if (!isDocumentsIncomplete && variant === 'notification') {
      // If notification variant is only intended to notify when incomplete,
      // return a sleek status card for other statuses
      return (
        <div
          data-testid="profile-status-banner"
          data-status={effectiveStatus}
          className={`p-4 rounded-2xl border transition-all ${config.cardBg} ${className}`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-black/40 border border-white/10 shrink-0">
                <StatusIcon className={`w-5 h-5 ${config.iconColor}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold">
                    Profile Status
                  </span>
                  <span className="text-slate-600">•</span>
                  <span className="text-xs font-bold font-mono text-white uppercase">
                    {config.badgeText}
                  </span>
                </div>
                <p className="text-xs text-slate-300 mt-0.5">{config.alertDesc}</p>
              </div>
            </div>
            {onNavigateToVerification && (
              <button
                type="button"
                onClick={onNavigateToVerification}
                className="self-start sm:self-center px-3.5 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0"
              >
                <span>Verification Center</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      );
    }

    // High-visibility Incomplete Documents Notification Banner
    return (
      <div
        data-testid="profile-status-incomplete-notification"
        data-status={effectiveStatus}
        role="alert"
        className={`p-4 sm:p-5 rounded-2xl border relative overflow-hidden shadow-xl transition-all ${config.cardBg} ${className}`}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-2xl bg-amber-950 text-amber-400 border border-amber-800/80 shrink-0 mt-0.5 animate-pulse">
              <FileWarning className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-950 text-amber-300 border border-amber-600 uppercase tracking-wide">
                  Action Required
                </span>
                <span className="text-xs font-black text-amber-200">
                  {config.alertTitle}
                </span>
                {missingCount > 0 && (
                  <span
                    data-testid="missing-sides-count-tag"
                    className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-rose-950 text-rose-300 border border-rose-800"
                  >
                    {missingCount} Side{missingCount !== 1 ? 's' : ''} Missing
                  </span>
                )}
              </div>

              <p className="text-xs text-amber-200/90 leading-relaxed">
                Your profile status is currently{' '}
                <code className="font-mono font-bold text-amber-300 bg-black/40 px-1 py-0.5 rounded">
                  documents_incomplete
                </code>
                . In accordance with platform safety policies, all mandatory document sides
                must be completed before your profile can be submitted for review.
              </p>

              {/* Incomplete Document Sides List */}
              {missingItems.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5 pt-1">
                  {missingItems.map((item, idx) => (
                    <span
                      key={`${item.documentId}-${item.side}-${idx}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/60 border border-rose-800/70 text-rose-300 text-[11px] font-mono"
                    >
                      <XCircle className="w-3 h-3 text-rose-400 shrink-0" />
                      <span>
                        <strong className="text-white">{item.documentTitle}</strong> (
                        {item.side.toUpperCase()})
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action CTA */}
          {onNavigateToVerification && (
            <div className="shrink-0 flex items-center gap-2">
              <button
                type="button"
                data-testid="notification-upload-missing-btn"
                onClick={onNavigateToVerification}
                className="w-full sm:w-auto px-4 py-2 rounded-xl bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 text-white text-xs font-bold font-mono transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-amber-950/60"
              >
                <Upload className="w-3.5 h-3.5" />
                <span>Upload Missing Documents</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // =========================================================================
  // VARIANT: PILL / CHIP / COMPACT / STANDARD BADGE
  // =========================================================================
  return (
    <div
      ref={popoverRef}
      className={`relative inline-block ${className}`}
      data-testid="profile-status-badge-container"
    >
      <div
        data-testid="profile-status-badge"
        data-profile-status={effectiveStatus}
        onClick={() => {
          if (interactive && isDocumentsIncomplete && missingItems.length > 0) {
            setIsPopoverOpen((prev) => !prev);
          } else if (interactive) {
            handleAction();
          }
        }}
        className={`inline-flex items-center font-mono font-bold rounded-full border shadow-xs transition-all ${sizeClasses} ${config.bg} ${
          interactive ? 'cursor-pointer' : ''
        }`}
        title={`Profile Status: ${config.label}${
          isDocumentsIncomplete && missingCount > 0 ? ` (${missingCount} missing document sides)` : ''
        }`}
      >
        {/* Pulsing Status Dot */}
        <span className={`rounded-full shrink-0 ${dotSize} ${config.dot}`} />

        {/* Optional Icon */}
        {showIcon && <StatusIcon className={`${iconSize} shrink-0 ${config.iconColor}`} />}

        {/* Status Text */}
        <span className="uppercase tracking-wider">
          {variant === 'compact' ? config.shortText : config.badgeText}
        </span>

        {/* Missing items badge count pill for documents_incomplete */}
        {isDocumentsIncomplete && showMissingCount && missingCount > 0 && (
          <span
            data-testid="badge-missing-count"
            className="px-1.5 py-0.2 rounded-full text-[10px] bg-rose-950 text-rose-300 border border-rose-800 font-black flex items-center gap-0.5 ml-0.5"
          >
            <span>{missingCount}</span>
            <span className="hidden sm:inline">Missing</span>
          </span>
        )}

        {/* Dropdown Chevron if incomplete with missing items */}
        {interactive && isDocumentsIncomplete && missingItems.length > 0 && (
          <ChevronDown
            className={`w-3 h-3 text-amber-400 transition-transform ${
              isPopoverOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </div>

      {/* ===================================================================== */}
      {/* INCOMPLETE STATUS INTERACTIVE NOTIFICATION POPOVER */}
      {/* ===================================================================== */}
      {isPopoverOpen && isDocumentsIncomplete && (
        <div
          data-testid="profile-status-popover"
          className="absolute z-50 mt-2 left-0 sm:right-0 sm:left-auto w-72 sm:w-80 p-4 rounded-2xl bg-slate-950 border border-amber-600/70 shadow-2xl space-y-3 animate-in fade-in zoom-in-95 text-left"
        >
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <div className="flex items-center gap-2">
              <FileWarning className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-bold font-mono text-amber-300 uppercase">
                Missing Document Sides ({missingCount})
              </span>
            </div>
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-950 text-rose-300 border border-rose-800">
              Mandatory
            </span>
          </div>

          <p className="text-[11px] text-slate-300 leading-relaxed">
            The following mandatory document sides are required to complete your verification:
          </p>

          <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
            {missingItems.map((item, idx) => (
              <li
                key={`${item.documentId}-${item.side}-${idx}`}
                className="p-2 rounded-xl bg-slate-900 border border-rose-900/60 flex items-start gap-2 text-xs"
              >
                <XCircle className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-white flex items-center justify-between">
                    <span>{item.documentTitle}</span>
                    <span className="text-[10px] font-mono uppercase text-rose-400 font-bold">
                      {item.side} missing
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-400">{item.reason || item.label}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="pt-2 border-t border-white/10 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setIsPopoverOpen(false)}
              className="px-2.5 py-1 rounded-lg text-[11px] text-slate-400 hover:text-white transition-colors cursor-pointer"
            >
              Close
            </button>
            {onNavigateToVerification && (
              <button
                type="button"
                onClick={() => {
                  setIsPopoverOpen(false);
                  onNavigateToVerification();
                }}
                className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-mono font-bold text-[11px] flex items-center gap-1.5 transition-all cursor-pointer shadow-md shadow-amber-950"
              >
                <Upload className="w-3 h-3" />
                <span>Upload Now</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileStatusBadge;
